<template>
    <div id="language-selector">
        <v-menu open-on-click location="bottom">
            <template #activator="{ props }">
                <v-btn icon variant="tonal" rounded color="white" v-bind="props" size="small">
                    <v-avatar rounded="md" color="transparent">
                        <img
                            v-if="activeLocaleFlag"
                            :src="getFlag(activeLocaleFlag)"
                            alt="Language Flag"
                            width="22"
                            height="15"
                            class="obj-cover"
                        />
                    </v-avatar>
                </v-btn>
            </template>
            <v-sheet rounded="md" width="200" elevation="10">
                <v-list class="theme-list">
                    <v-list-item
                        v-for="(item, index) in LOCALES"
                        :key="index"
                        color="primary"
                        :active="locale == item.key"
                        class="d-flex align-center"
                        @click="changeLanguage(item)"
                    >
                        <template #prepend>
                            <v-avatar rounded="md">
                                <img
                                    :src="getFlag(item.avatar)"
                                    :alt="item.label"
                                    width="22"
                                    height="15"
                                    class="obj-cover"
                                />
                            </v-avatar>
                        </template>
                        <v-list-item-title class="text-subtitle-1 font-weight-regular">
                            {{ item.label }}
                            <span class="text-disabled text-subtitle-1 pl-2">({{ item.key_label }})</span>
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-sheet>
        </v-menu>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useLocale } from 'vuetify';
import { storeToRefs } from 'pinia';
import { forEach, find, includes, toUpper, split } from 'lodash';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import { i18n, defaultLocale } from '../../../lang';
import useUIStore from '../store/ui.store';
import usFlag from '../../../assets/us.svg';
import caFlag from '../../../assets/ca.svg';
import esFlag from '../../../assets/es.svg';

const uiStore = useUIStore();
const { current } = useLocale();

const { locale } = storeToRefs(uiStore);

const localStorage = new LocalStorageService();

const LOCALES: {
    vuetify_key: string;
    key: string;
    label: string;
    key_label: string;
    avatar: string;
}[] = [];

const activeLocaleFlag = ref('us');

onMounted(() => {
    init();
});

function init(): void {
    const availableLocales: string[] = i18n.global.availableLocales;
    forEach(availableLocales, (locale) => {
        const [key, label, flag] = split(locale, '_');
        LOCALES.push({
            key: locale as string,
            label: label as string,
            key_label: toUpper(flag) as string,
            vuetify_key: key,
            avatar: flag as string,
        });
    });
    const selectedLocale: string = localStorage.getItem('locale') as string;

    if (includes(availableLocales, selectedLocale)) {
        const localeObj:
            | {
                  vuetify_key: string;
                  key: string;
                  label: string;
                  key_label: string;
                  avatar: string;
              }
            | undefined = find(LOCALES, (x) => x.key === selectedLocale);
        if (localeObj === undefined) {
            return;
        }
        changeLanguage(localeObj);
    }
    if (!includes(availableLocales, selectedLocale)) {
        const defaultLocaleObj:
            | {
                  vuetify_key: string;
                  key: string;
                  label: string;
                  key_label: string;
                  avatar: string;
              }
            | undefined = find(LOCALES, (x) => x.key === defaultLocale);
        if (defaultLocaleObj === undefined) {
            return;
        }
        changeLanguage(defaultLocaleObj);
    }
}

function getFlag(key: string): string {
    switch (key) {
        case 'us':
            return usFlag;
        case 'ca':
            return caFlag;
        case 'es':
            return esFlag;
    }

    return esFlag;
}

function changeLanguage(locale: {
    vuetify_key: string;
    key: string;
    label: string;
    key_label: string;
    avatar: string;
}): void {
    uiStore.setLocale(locale.key as string);
    current.value = locale.vuetify_key as string;
    localStorage.setItem('locale', locale.key as string);
    activeLocaleFlag.value = locale.avatar;
}
</script>
<style lang="scss"></style>
